import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiActiveZoneModule, TuiLetModule } from '@taiga-ui/cdk';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  delay,
  filter,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AsyncPipe, NgForOf } from '@angular/common';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  TuiDataListDropdownManagerModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiToggleModule,
} from '@taiga-ui/kit';
import { fromFormControlValue } from '@shared/utils/observables';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiGroupModule,
  TuiHintModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import CatalogsState from '@store/catalogs/catalogs.state';
import { Store } from '@ngxs/store';
import { CargoPlaceType } from '@shared/constants/cargo-places';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AlertService } from '@shared/services/alert.service';
import { FormsService } from '@shared/services/forms.service';
import { CompareWithFieldDirective } from '@shared/directives/compare-with-field.directive';
import {
  BuyerFormComponent,
  BuyerFormInputData,
} from '@shared/components/catalogs/buyer-form/buyer-form.component';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { InputDateComponent } from '@shared/components/forms/input-date/input-date.component';
import {
  CargoOrder,
  CargoOrderFormWithFactPackages,
  OrderCargoPlaceDetails,
  OrderCargoPlaceDetailsFormWithFactPackageForm,
} from '../../../../../../types/cargo';
import { CargoCalculationService } from '../../../../services/cargo-calculation.service';
import {
  FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED,
  factPackageQuantityValidator,
} from '../../validators/fact-package-quantity.validator';
import { CargoPlaceDetailsComponent } from '../cargo-place-details/cargo-place-details.component';

@Component({
  selector: 'app-order',
  standalone: true,
  imports: [
    TuiActiveZoneModule,
    AsyncPipe,
    AutocompleteComponent,
    TuiToggleModule,
    ReactiveFormsModule,
    CheckboxComponent,
    TuiGroupModule,
    TuiInputNumberModule,
    TuiTextfieldControllerModule,
    TuiHintModule,
    TuiLetModule,
    CompareWithFieldDirective,
    TuiDataListDropdownManagerModule,
    TuiDataListModule,
    TuiDropdownModule,
    TuiButtonModule,
    NgForOf,
    CargoPlaceDetailsComponent,
    TuiInputModule,
    InputDateComponent,
  ],
  templateUrl: './order.component.html',
  styleUrl: './order.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderComponent implements OnInit {
  update$ = new Subject<void>();
  isActiveZone$ = new BehaviorSubject(false);
  openCreateBuyerDialog$ = new Subject<void>();
  hasBox$: Observable<boolean>;

  sellers$ = this.catalogs.sellers$;

  orderForm = this.fb.group<CargoOrderFormWithFactPackages>({
    farmBrokerJson: this.forms.getFarmJsonForm(),
    prepaymentCurrencyLetter: this.fb.control(null),
    prepaymentSum: this.fb.control(null),
    brokerJson: this.forms.getSellerJsonForm(),
    expectedLoadingDate: this.fb.control(null),
    paymentDate: this.fb.control(null),
    prepayment: this.fb.control(null),
    id: this.fb.control<Nullable<number>>(null),
    uniqueKey: this.fb.control(null),
    cargoId: this.fb.control<Nullable<number>>(null),
    countryLetter: this.fb.control(''),
    airWaybillNumber: this.fb.control(''),
    precooling: this.fb.control<Nullable<boolean>>(null),
    weight: this.fb.control<Nullable<number>>(null),
    buyerCode: this.fb.control(''),
    farmJson: this.forms.getFarmJsonForm(),
    farmAddress: this.forms.getFarmAddressForm(),
    clientJson: this.forms.getClientJsonForm(),
    flowerType: this.fb.control(''),
    imported: this.fb.control<Nullable<boolean>>({
      value: null,
      disabled: true,
    }),
    cold: this.fb.control<Nullable<boolean>>(null),
    clientAddressWithCoordinatesJson:
      this.forms.getClientAddressWithCoordinatesJson(),
    attachmentId: this.fb.control<Nullable<number>>(null),
    invoiceCost: this.fb.control<Nullable<number>>(null),
    invoiceCurrencyLetter: this.fb.control<Nullable<string>>(null),
    invoiceDate: this.fb.control<Nullable<string>>(null),
    invoiceNumber: this.fb.control<Nullable<string>>(null),
    orderGroupId: this.fb.control<Nullable<number>>(null),
    preOrderId: this.fb.control<Nullable<number>>(null),
    rateJson: this.forms.getRateJsonForm(),
    precoolingWeight: this.fb.control<Nullable<number>>(null),
    cargoPlacesJson: this.fb.array<
      FormGroup<OrderCargoPlaceDetailsFormWithFactPackageForm>
    >([]),
    divisionId: this.fb.control(null),
  });

  isImport$ = fromFormControlValue(this.orderForm.controls.imported);

  availableFarmAddresses$ = fromFormControlValue(
    this.orderForm.controls.farmJson.controls.addressesJson,
  );

  availableAddresses$ =
    this.addressesWithCoordinatesJsonControl.valueChanges.pipe(
      startWith([]),
      map(() => this.addressesWithCoordinatesJsonControl.getRawValue()),
    );

  preparedPackageTypes$ = this.catalogs.packageTypes$.pipe(
    map(packageTypes =>
      packageTypes.map(packageType => ({
        ...packageType,
        itemText: `${packageType.code} - ${packageType.name}`,
      })),
    ),
  );

  availableFlowerTypes$ = combineLatest([
    this.catalogs.flowerTypes$,
    fromFormControlValue(
      this.orderForm.controls.farmJson.controls.flowerTypesJson,
    ),
  ]).pipe(
    map(([flowerTypes, farmFlowerTypeIds]) => {
      if (
        !flowerTypes.length ||
        !farmFlowerTypeIds ||
        !farmFlowerTypeIds.length
      ) {
        return [];
      }
      return flowerTypes.filter(flowerType => {
        return farmFlowerTypeIds.includes(flowerType.id.toString());
      });
    }),
  );

  buyersOfSelectedFarm$ = combineLatest([
    fromFormControlValue(this.orderForm.controls.farmJson.controls.id),
    fromFormControlValue(this.orderForm.controls.clientJson.controls.id),
    this.catalogs.buyers$,
  ]).pipe(
    map(([farmId, clientId, buyers]) => {
      if (!farmId || !clientId) {
        return [];
      }
      return buyers.filter(
        buyer => buyer.farmId === farmId && buyer.clientId === clientId,
      );
    }),
  );

  countries = this.store.selectSignal(CatalogsState.getNsiCountries);
  cargoPlaceQuantityLimit = this.fb.control<Nullable<number>>({
    value: null,
    disabled: true,
  });

  protected readonly FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED =
    FACT_PACKAGE_QUANTITY_GREATER_THAN_ESTIMATED;

  constructor(
    protected readonly catalogs: CargoCatalogsService,
    private readonly fb: FormBuilder,
    private readonly store: Store,
    private readonly alert: AlertService,
    private readonly destroyRef: DestroyRef,
    private readonly forms: FormsService,
    private readonly cargoCalculationService: CargoCalculationService,
    private readonly dialogs: TuiDialogService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  onFarmAddressChange(address: Nullable<string>) {
    this.setFarmAddressDetails(address);
  }

  onFarmChange() {
    const id =
      this.orderForm.controls.farmJson.controls.id.value?.toString() ?? '';
    this.setFarmDetails(id);
    this.clearFarmAddress();
    this.clearClientCodes();
    this.setDefaultCargoPlaceIfImport();
  }

  onAddressChange() {
    // const address =
    //   this.clientAddressWithCoordinatesJson.controls.address.value?.toString() ??
    //   '';
    //
    // if (!address) {
    //   this.clientAddressWithCoordinatesJson.reset();
    // } else {
    //   const addresses = this.addressesWithCoordinatesJsonControl.getRawValue();
    //   const details = addresses.find(a => a.address === address);
    //   if (details) {
    //     this.clientAddressWithCoordinatesJson.patchValue(details);
    //   } else {
    //     this.clientAddressWithCoordinatesJson.reset();
    //   }
    // }
    // this.clientAddressWithCoordinatesJson.markAsDirty();
  }

  onClientChange() {
    const id =
      this.orderForm.controls.clientJson.controls.id.value?.toString() ?? '';
    this.setClientName(id);
    this.clearClientCodes();
    this.clearClientAddress();
  }

  onZoneChange(focused: boolean) {
    if (!focused) {
      this.update$.next();
    }
    this.isActiveZone$.next(focused);
  }

  onUpdate() {
    this.update$
      .pipe(
        delay(0),
        tap(() => {
          if (!this.orderForm.valid) {
            this.alert.showWarning(
              'Фактическое кол-во не соответствует лимиту',
            );
          }
        }),
        filter(() => !this.orderForm.pristine),
        switchMap(() => {
          const order = this.orderForm.getRawValue() as CargoOrder;
          return this.cargoCalculationService.updateOrder(order);
        }),
        tap(() => {
          this.alert.showSuccess();
          this.orderForm.markAsUntouched();
          this.orderForm.markAsPristine();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onCreateBuyerClick() {
    this.openCreateBuyerDialog$
      .pipe(
        switchMap(() => {
          const data: BuyerFormInputData = {
            buyer: {
              id: null,
              uniqueKey: null,
              clientId: this.clientJson.controls.id.value,
              farmId: this.farmJson.controls.id.value,
              code: this.orderForm.controls.buyerCode.value,
              name: null,
              publicAccess: null,
            },
          };
          return this.dialogs.open(
            new PolymorpheusComponent(BuyerFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data,
            },
          );
        }),
        tap(() => this.catalogs.fetchBuyers$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  clearFarmAddress() {
    this.farmAddress.reset();
  }

  get farmAddress() {
    return this.orderForm.controls.farmAddress;
  }

  setFarmAddressDetails(addressJson: Nullable<string>) {
    if (!addressJson) {
      this.farmAddress.reset();
    } else {
      const farmAddress = this.farmJson.controls.addressesJson.value.find(
        a => a.address === addressJson,
      );
      farmAddress
        ? this.farmAddress.patchValue(farmAddress)
        : this.farmAddress.reset();
    }
    this.farmAddress.markAsDirty();
  }

  get addressesWithCoordinatesJsonControl() {
    return this.orderForm.controls.clientJson.controls
      .addressesWithCoordinatesJson;
  }

  clearClientAddress() {
    this.orderForm.controls.clientAddressWithCoordinatesJson.reset();
  }

  get farmJson() {
    return this.orderForm.controls.farmJson;
  }

  get clientJson() {
    return this.orderForm.controls.clientJson;
  }

  setFarmDetails(farmId: Nullable<string>) {
    if (!farmId) {
      this.orderForm.patchValue({
        flowerType: null,
        imported: null,
      });
      this.farmJson.reset();
    } else {
      const farm = this.catalogs.farmMap.get(farmId.toString());
      if (farm) {
        this.farmJson.patchValue(farm);
        this.orderForm.patchValue({
          flowerType: null,
          imported: farm.imported,
        });
      } else {
        this.farmJson.reset();
      }
    }
    this.farmJson.markAsDirty();
  }

  setDefaultCargoPlaceIfImport() {
    if (this.orderForm.controls.imported.getRawValue()) {
      const cargoPlace = this.catalogs.cargoPlaceMap.get(
        CargoPlaceType.PALLET_US,
      );

      this.orderForm.controls.cargoPlacesJson.controls.forEach(details => {
        if (cargoPlace) {
          details.controls.cargoPlaceJson.patchValue(cargoPlace);
        } else {
          details.controls.cargoPlaceJson.reset();
        }
      });
    }
  }

  setClientName(id: Nullable<string>) {
    if (!id) {
      this.clientJson.reset();
    } else {
      const client = this.catalogs.clientMap.get(id.toString());
      if (client) {
        this.clientJson.patchValue(client);
      } else {
        this.clientJson.reset();
      }
    }
    this.clientJson.markAsDirty();
  }

  clearClientCodes() {
    this.orderForm.controls.buyerCode.setValue(null);
  }

  disableFieldsInCargoPlaceForm(
    form: FormGroup<OrderCargoPlaceDetailsFormWithFactPackageForm>,
  ) {
    const keys: Array<keyof OrderCargoPlaceDetails> = [
      'factPackageTypesJson',
      'factCargoPlaceQuantity',
    ];
    Object.keys(form.controls)
      .filter(k => !keys.includes(k as keyof OrderCargoPlaceDetails))
      .forEach(k => {
        form.controls[k as keyof OrderCargoPlaceDetails].disable({
          emitEvent: false,
        });
      });
    form.controls.factPackageTypesJson.controls.forEach(fact => {
      fact.controls.packageTypeJson.controls.name.disable();
    });
  }

  disableFieldsInOrderForm() {
    const keys: Array<keyof CargoOrder> = [
      'cargoPlacesJson',
      'precoolingWeight',
    ];
    Object.keys(this.orderForm.controls)
      .filter(k => !keys.includes(k as keyof CargoOrder))
      .forEach(k => {
        this.orderForm.controls[k as keyof CargoOrder].disable({
          emitEvent: false,
        });
      });
  }

  @Input({ required: true }) set order(order: CargoOrder) {
    const { cargoPlacesJson, ...rest } = order;
    this.orderForm.patchValue(rest, { emitEvent: false });
    const cargoPlacesJsonControl = this.orderForm.controls.cargoPlacesJson;
    cargoPlacesJsonControl.clear({ emitEvent: false });
    cargoPlacesJson.forEach((details, index, array) => {
      const form =
        this.forms.getPreOrderCargoPlaceDetailsFormWithFactPackagesForm(
          details,
        );
      form.addValidators([factPackageQuantityValidator()]);
      this.disableFieldsInCargoPlaceForm(form);
      cargoPlacesJsonControl.push(form, {
        emitEvent: index === array.length - 1,
      });
    });
    this.disableFieldsInOrderForm();
  }

  fillBrokerJson(sellerId: Nullable<string>) {
    const sellerJsonControl = this.orderForm.controls.brokerJson;
    const seller =
      this.catalogs.sellerMap.get(sellerId?.toString() ?? '') ?? null;
    if (seller) {
      sellerJsonControl.patchValue(seller);
    } else {
      sellerJsonControl.reset();
      sellerJsonControl.markAsDirty();
    }
  }

  ngOnInit() {
    this.onUpdate();
    this.onCreateBuyerClick();
  }
}
